import React from "react";
import { useSelector, useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import { Box, Typography, IconButton } from "@mui/material";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import { addToCart } from "../../Store/reducers/cartReducer";
import { useAddToWishlist } from "../../utils/wishlistUtils";
import { FormattedMessage, useIntl } from "react-intl";
import { notifyError } from "../../utils/notify";
import { buildSrcSet } from "../../utils/imageSrcSet";

const API_URL = process.env.REACT_APP_API_URL;
const UPLOAD_URL = process.env.REACT_APP_UPLOAD_URL;
// Grid is 2 columns on phones, 3 on tablets and 4 from md up.
const CARD_SIZES = "(max-width: 599px) 50vw, (max-width: 899px) 33vw, 25vw";

// Boutique product tile: flat image on a soft paper ground, serif name, quiet
// price and an "add to bag" bar that slides up on hover (always shown on touch).
// `eager` starts the main image download immediately instead of when the tile nears the viewport.
const Card = ({ item, showPrice = true, eager = false }) => {
  const currency = useSelector((state) => state.currency.selectedCurrency);
  const rates = useSelector((state) => state.currency.rates);
  const symbols = useSelector((state) => state.currency.symbols);
  const addToWishlist = useAddToWishlist(API_URL);
  const dispatch = useDispatch();
  const intl = useIntl();
  const inCart = useSelector((state) => state.cart.products.find((p) => p.id === item.id));
  const stock = item.stock;
  const isOutOfStock = stock != null && stock <= 0;

  const convert = (price) =>
    rates && rates[currency] ? (price * rates[currency]).toFixed(2) : price;

  const handleAddToCart = () => {
    if (isOutOfStock) {
      notifyError(intl.formatMessage({ id: "out_of_stock" }));
      return;
    }
    if (stock != null && inCart && inCart.quantity >= stock) {
      notifyError(intl.formatMessage({ id: "stock_limit_reached" }, { count: stock }));
      return;
    }
    dispatch(
      addToCart({
        id: item.id,
        documentId: item.documentId,
        title: item.title,
        new_price: item.new_price ?? item.price,
        img: item?.img1?.url,
        stock: item.stock,
        quantity: 1,
      })
    );
  };

  const symbol = symbols[currency] || currency;
  const hasDiscount = item?.price && item?.new_price && Number(item.price) > Number(item.new_price);
  const secondImg = item?.img2?.url;

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        bgcolor: "background.default",
        "&:hover .card-media img.primary": secondImg ? { opacity: 0 } : { transform: "scale(1.03)" },
        "&:hover .card-media img.secondary": { opacity: 1 },
        "&:hover .card-add, &:focus-within .card-add": { transform: "translateY(0)", opacity: 1 },
      }}
    >
      <Box
        className="card-media"
        sx={{
          position: "relative",
          width: "100%",
          aspectRatio: "4 / 5",
          overflow: "hidden",
          bgcolor: "background.paper",
          img: {
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            transition: "opacity .6s var(--ease), transform 1.2s var(--ease)",
          },
          "img.secondary": { opacity: 0 },
        }}
      >
        <Link to={`/product/${item.slug || item.documentId}`} aria-label={item.title}>
          <img
            className="primary"
            src={UPLOAD_URL + (item?.img1?.formats?.medium?.url || item?.img1?.url)}
            srcSet={buildSrcSet(item?.img1)}
            sizes={CARD_SIZES}
            alt={item.title}
            loading={eager ? "eager" : "lazy"}
            decoding="async"
          />
          {secondImg && (
            <img
              className="secondary"
              src={UPLOAD_URL + (item?.img2?.formats?.medium?.url || secondImg)}
              srcSet={buildSrcSet(item?.img2)}
              sizes={CARD_SIZES}
              alt=""
              loading="lazy"
              decoding="async"
            />
          )}
        </Link>

        {item?.isNew && (
          <Typography
            component="span"
            sx={{
              position: "absolute",
              top: 14,
              left: 14,
              zIndex: 2,
              fontSize: "0.6rem",
              letterSpacing: "0.22em",
              textTransform: "uppercase",
              color: "text.primary",
              pointerEvents: "none",
            }}
          >
            <FormattedMessage id="badge_new" defaultMessage="Nowe" />
          </Typography>
        )}

        <IconButton
          onClick={() => addToWishlist(item.documentId)}
          aria-label={intl.formatMessage({ id: "add_to_wishlist" })}
          sx={{ position: "absolute", top: 4, right: 4, zIndex: 2 }}
        >
          <FavoriteBorderIcon sx={{ fontSize: "1.1rem" }} />
        </IconButton>

        <Box
          component="button"
          className="card-add"
          onClick={handleAddToCart}
          disabled={isOutOfStock}
          aria-label={intl.formatMessage({ id: isOutOfStock ? "out_of_stock" : "add_to_cart" })}
          sx={{
            position: "absolute",
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 2,
            py: "14px",
            border: "none",
            cursor: "pointer",
            bgcolor: "text.primary",
            color: "background.default",
            fontFamily: "var(--font-sans)",
            fontSize: "0.64rem",
            letterSpacing: "0.2em",
            textTransform: "uppercase",
            transform: "translateY(100%)",
            opacity: 0,
            transition: "transform .45s var(--ease), opacity .45s var(--ease)",
            "@media (hover: none)": { transform: "none", opacity: 1, py: "10px" },
            "&:disabled": { cursor: "not-allowed", bgcolor: "text.secondary" },
          }}
        >
          <FormattedMessage id={isOutOfStock ? "out_of_stock_short" : "add_to_bag"} />
        </Box>
      </Box>

      <Box
        component={Link}
        to={`/product/${item.slug || item.documentId}`}
        sx={{
          pt: "16px",
          pb: "4px",
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "6px",
          textAlign: "center",
          textDecoration: "none",
          color: "text.primary",
        }}
      >
        <Typography
          component="h3"
          sx={{
            fontFamily: "var(--font-serif)",
            fontWeight: 400,
            fontSize: { xs: "1.05rem", sm: "1.2rem" },
            lineHeight: 1.25,
            width: "100%",
            overflow: "hidden",
            whiteSpace: "nowrap",
            textOverflow: "ellipsis",
          }}
        >
          {item.title}
        </Typography>

        {showPrice && (
          // Wraps to two lines on narrow tiles; minHeight keeps every tile in a row the same height.
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "center",
              columnGap: "10px",
              alignItems: "baseline",
              alignContent: "flex-start",
              minHeight: { xs: "2.6em", sm: "1.3em" },
              lineHeight: 1.3,
              fontSize: "0.8rem",
              letterSpacing: "0.06em",
              "& > span": { whiteSpace: "nowrap" },
            }}
          >
            {hasDiscount && (
              <Box component="span" sx={{ color: "text.secondary", textDecoration: "line-through" }}>
                {convert(item.price)} {symbol}
              </Box>
            )}
            <Box component="span" sx={{ fontWeight: 400 }}>
              {convert(item?.new_price ?? item?.price)} {symbol}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default React.memo(Card);
